import { useState, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore, selectBusinesses } from '@/utils/store';
import { CATEGORY_ICONS } from '@/utils/format';
import { processReceipt } from '@/utils/receiptOcr';
import Modal from '@/components/ui/Modal';
import Button from '@/components/ui/Button';
import MemberMultiSelect from '@/components/ui/MemberMultiSelect';
import { Camera, Upload, X, ChevronDown, ChevronUp, Loader2 } from 'lucide-react';

const EXPENSE_CATEGORIES = ['food', 'transport', 'housing', 'entertainment', 'health', 'education', 'shopping', 'services', 'credit', 'other'];

export default function ScanReceiptModal({ open, onClose }) {
  const { t } = useTranslation();
  const addTransaction = useStore((s) => s.addTransaction);
  const businesses = useStore(selectBusinesses);
  const businessEnabled = useStore((s) => s.appSettings?.businessEnabled === true);

  const [step, setStep] = useState(1);
  const [capturedImage, setCapturedImage] = useState(null);
  const [cameraActive, setCameraActive] = useState(false);
  const [progress, setProgress] = useState({ status: '', progress: 0, percent: 0 });
  const [ocrResult, setOcrResult] = useState(null);
  const [error, setError] = useState('');

  // Form fields (step 3)
  const [store, setStore] = useState('');
  const [amount, setAmount] = useState('');
  const [category, setCategory] = useState('');
  const [description, setDescription] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [memberIds, setMemberIds] = useState([]);
  const [businessId, setBusinessId] = useState('');
  const [showItems, setShowItems] = useState(false);

  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const fileInputRef = useRef(null);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setCameraActive(false);
  }, []);

  const handleClose = useCallback(() => {
    stopCamera();
    setStep(1);
    setCapturedImage(null);
    setProgress({ status: '', progress: 0, percent: 0 });
    setOcrResult(null);
    setError('');
    setStore('');
    setAmount('');
    setCategory('');
    setDescription('');
    setDate(new Date().toISOString().split('T')[0]);
    setMemberIds([]);
    setBusinessId('');
    setShowItems(false);
    onClose();
  }, [onClose, stopCamera]);

  useEffect(() => {
    if (!open) stopCamera();
  }, [open, stopCamera]);

  const startCamera = async () => {
    try {
      setError('');
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } }
      });
      streamRef.current = stream;
      setCameraActive(true);
      // Wait for next render so videoRef is mounted, then attach stream
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.onloadedmetadata = () => {
            videoRef.current.play().catch(() => {});
          };
        }
      }, 100);
    } catch {
      setError(t('scan.cameraError'));
    }
  };

  const capturePhoto = () => {
    if (!videoRef.current) return;
    const video = videoRef.current;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
    setCapturedImage(dataUrl);
    stopCamera();
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setCapturedImage(ev.target.result);
    reader.readAsDataURL(file);
  };

  const analyzeReceipt = async () => {
    if (!capturedImage) return;
    setStep(2);
    setError('');

    try {
      const result = await processReceipt(capturedImage, (p) => setProgress(p));
      setOcrResult(result);
      setStore(result.store || '');
      setAmount(result.total || '');
      setCategory(result.suggestedCategory || 'other');
      setDescription(result.description || result.store || '');
      setDate(result.date || new Date().toISOString().split('T')[0]);
      setStep(3);
    } catch {
      setError(t('scan.noTextFound'));
      setStep(1);
    }
  };

  const handleSave = () => {
    if (!amount || Number(amount) <= 0) return;
    addTransaction({
      description: description || store || t(`categories.${category}`),
      amount: Number(amount),
      type: 'expense',
      category: category || 'other',
      date,
      ...(businessId ? { businessId } : {}),
      ...(!businessId && memberIds.length > 0 ? { memberIds } : { memberIds: [] }),
    });
    handleClose();
  };

  const resetToStep1 = () => {
    stopCamera();
    setStep(1);
    setCapturedImage(null);
    setProgress({ status: '', progress: 0, percent: 0 });
    setOcrResult(null);
    setError('');
  };

  const getStatusText = () => {
    switch (progress.status) {
      case 'optimizing': return t('scan.optimizing');
      case 'recognizing': return `${t('scan.recognizing')} ${progress.percent || 0}%`;
      case 'analyzing': return t('scan.analyzing');
      case 'completed': return t('scan.completed');
      default: return t('scan.processing');
    }
  };

  const confidenceColor = ocrResult
    ? ocrResult.confidence >= 70 ? 'text-success-500 bg-success-50 dark:bg-success-500/10'
    : ocrResult.confidence >= 40 ? 'text-warning-500 bg-warning-50 dark:bg-warning-500/10'
    : 'text-danger-500 bg-danger-50 dark:bg-danger-500/10'
    : '';

  return (
    <Modal open={open} onClose={handleClose} title={t('scan.title')}>
      <div className="space-y-4">
        {/* Step 1: Capture */}
        {step === 1 && (
          <>
            {error && (
              <div className="p-3 rounded-xl bg-danger-50 dark:bg-danger-500/10 text-danger-600 dark:text-danger-400 text-sm">
                {error}
              </div>
            )}

            {cameraActive ? (
              <div className="space-y-2">
                <div
                  className="relative rounded-xl overflow-hidden bg-black aspect-[4/5] cursor-pointer"
                  onClick={capturePhoto}
                >
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-4 border-2 border-white/30 rounded-xl pointer-events-none" />
                  <div className="absolute bottom-3 left-0 right-0 text-center text-white/70 text-xs font-medium pointer-events-none">
                    {t('scan.tapToCapture')}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button className="flex-1" onClick={capturePhoto} icon={Camera}>
                    {t('scan.capture')}
                  </Button>
                  <Button variant="ghost" onClick={stopCamera} icon={X}>
                    {t('scan.cancel')}
                  </Button>
                </div>
              </div>
            ) : capturedImage ? (
              <div className="space-y-2">
                <div className="flex gap-2">
                  <Button className="flex-1" onClick={analyzeReceipt}>
                    {t('scan.analyze')}
                  </Button>
                  <Button variant="ghost" onClick={resetToStep1} icon={X}>
                    {t('scan.cancel')}
                  </Button>
                </div>
                <div className="rounded-xl overflow-hidden bg-gray-100 dark:bg-gray-800">
                  <img src={capturedImage} alt="Receipt" className="w-full max-h-80 object-contain" />
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <button
                  onClick={startCamera}
                  className="w-full p-6 rounded-2xl border-2 border-dashed border-brand-300 dark:border-brand-500/40 bg-brand-50/50 dark:bg-brand-500/5 hover:bg-brand-50 dark:hover:bg-brand-500/10 transition-colors flex flex-col items-center gap-3"
                >
                  <div className="w-14 h-14 rounded-2xl bg-brand-100 dark:bg-brand-500/20 flex items-center justify-center">
                    <Camera size={28} className="text-brand-600 dark:text-brand-400" />
                  </div>
                  <span className="text-sm font-medium text-brand-700 dark:text-brand-300">
                    {t('scan.useCamera')}
                  </span>
                </button>

                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full p-6 rounded-2xl border-2 border-dashed border-gray-300 dark:border-gray-600 bg-gray-50/50 dark:bg-gray-800/50 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors flex flex-col items-center gap-3"
                >
                  <div className="w-14 h-14 rounded-2xl bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                    <Upload size={28} className="text-gray-500 dark:text-gray-400" />
                  </div>
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-300">
                    {t('scan.uploadImage')}
                  </span>
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  className="hidden"
                  onChange={handleFileUpload}
                />
              </div>
            )}
          </>
        )}

        {/* Step 2: Processing */}
        {step === 2 && (
          <div className="py-8 flex flex-col items-center gap-4">
            <Loader2 size={48} className="text-brand-500 animate-spin" />
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {getStatusText()}
            </p>
            <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
              <div
                className="bg-brand-500 h-2 rounded-full transition-all duration-300"
                style={{ width: `${Math.round(progress.progress * 100)}%` }}
              />
            </div>
          </div>
        )}

        {/* Step 3: Results */}
        {step === 3 && ocrResult && (
          <>
            {/* Document type + Confidence badge */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                  {t('scan.resultsTitle')}
                </span>
                {ocrResult.documentType && (
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300">
                    {t(`scan.docType.${ocrResult.documentType}`)}
                  </span>
                )}
              </div>
              <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${confidenceColor}`}>
                {t('scan.confidence')}: {Math.round(ocrResult.confidence)}%
              </span>
            </div>

            {ocrResult.confidence < 40 && (
              <div className="p-2.5 rounded-xl bg-warning-50 dark:bg-warning-500/10 text-warning-600 dark:text-warning-400 text-xs">
                {t('scan.lowConfidence')}
              </div>
            )}

            {/* Store */}
            <div>
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 block">
                {t('scan.store')}
              </label>
              <input
                type="text"
                className="input"
                value={store}
                onChange={(e) => setStore(e.target.value)}
              />
            </div>

            {/* Amount */}
            <div>
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 block">
                {t('transactions.form.amount')}
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-2xl font-bold text-gray-300">$</span>
                <input
                  type="number"
                  inputMode="decimal"
                  placeholder="0"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="w-full pl-10 pr-4 py-4 text-2xl font-bold input"
                />
              </div>
            </div>

            {/* Category grid */}
            <div>
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 block">
                {t('transactions.form.category')}
              </label>
              <div className="grid grid-cols-3 gap-2">
                {EXPENSE_CATEGORIES.map((c) => (
                  <button
                    key={c}
                    onClick={() => setCategory(c)}
                    className={`py-2 px-3 text-xs font-medium rounded-xl border transition-all flex flex-col items-center gap-1 ${
                      category === c
                        ? 'border-brand-500 bg-brand-50 dark:bg-brand-500/10 text-brand-700 dark:text-brand-400'
                        : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-brand-400'
                    }`}
                  >
                    <span>{CATEGORY_ICONS[c] || '📌'}</span>
                    {t(`categories.${c}`)}
                  </button>
                ))}
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 block">
                {t('transactions.form.description')}
              </label>
              <input
                type="text"
                className="input"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>

            {/* Date */}
            <div>
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 block">
                {t('transactions.form.date')}
              </label>
              <input
                type="date"
                className="input"
                value={date}
                onChange={(e) => setDate(e.target.value)}
              />
            </div>

            {/* Business picker */}
            {businessEnabled && businesses.length > 0 && (
              <div>
                <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5 block">
                  {t('business.assignBusiness')}
                </label>
                <div className="flex gap-2 flex-wrap">
                  <button
                    onClick={() => setBusinessId('')}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                      !businessId
                        ? 'border-brand-500 bg-brand-50 dark:bg-brand-500/10 text-brand-700 dark:text-brand-400'
                        : 'border-gray-200 dark:border-gray-700 text-gray-500 hover:border-brand-400'
                    }`}
                  >
                    {t('business.personal')}
                  </button>
                  {businesses.map((b) => (
                    <button
                      key={b.id}
                      onClick={() => setBusinessId(b.id)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                        businessId === b.id
                          ? 'border-brand-500 bg-brand-50 dark:bg-brand-500/10 text-brand-700 dark:text-brand-400'
                          : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-brand-400'
                      }`}
                    >
                      <span>{b.icon}</span>
                      {b.name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Member picker */}
            {!businessId && (
              <MemberMultiSelect value={memberIds} onChange={setMemberIds} />
            )}

            {/* Items (collapsible) */}
            {ocrResult.items.length > 0 && (
              <div>
                <button
                  onClick={() => setShowItems(!showItems)}
                  className="flex items-center gap-2 text-xs font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                >
                  {t('scan.items')} ({ocrResult.items.length})
                  {showItems ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                </button>
                {showItems && (
                  <div className="mt-2 space-y-1">
                    {ocrResult.items.map((item, i) => (
                      <div key={i} className="flex justify-between text-xs py-1.5 px-2 rounded-lg bg-gray-50 dark:bg-gray-800/50">
                        <span className="text-gray-700 dark:text-gray-300 truncate flex-1">{item.name}</span>
                        <span className="text-gray-500 dark:text-gray-400 ml-2 font-medium">{item.priceFormatted}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-2 pt-2">
              <Button className="flex-1" onClick={handleSave}>
                {t('scan.saveExpense')}
              </Button>
              <Button variant="ghost" onClick={resetToStep1}>
                {t('scan.scanAnother')}
              </Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
